import { trackApparelDiscoveryShowroomV2View } from "@faire/web-api/events/apparelDiscovery/view/showroomV2";
import { trackRetailersPageViewView } from "@faire/web-api/events/retailers/view/pageView";
import { IBadge } from "@faire/web-api/indigofair/badges/IBadge";
import { IDisclaimer } from "@faire/web-api/indigofair/data/IDisclaimer";
import { IInformationMessage } from "@faire/web-api/indigofair/data/IInformationMessage";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import { IProductsResponse } from "@faire/web-api/indigofair/data/IProductsResponse";
import { Language } from "@faire/web-api/indigofair/languages/Language";
import { IProductVideo } from "@faire/web-api/indigofair/video/IProductVideo";
import { useStrictLocalization } from "@faire/web/common/localization";
import { useTraceComponentRender } from "@faire/web/common/tracing/useTraceComponentRender";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { IViewportV2Props } from "@faire/web/slate/viewport/withViewportV2";
import { useComputedObservable } from "@faire/web/ui/hooks/useComputedObservable";
import { useRefFrom } from "@faire/web/ui/hooks/useRefFrom";
import uniqBy from "lodash/uniqBy";
import { observer } from "mobx-react";
import React, { Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { LazyImageGallery } from "@faire/retailer/components/ImageGallery";
import { ISamplesBannerDetails } from "@faire/retailer/components/Modals/SamplesRequestModal/RequestSamplesCTA";
import { getAltTagProductPage } from "@faire/retailer/features/Marketplace/utils";
import { useCartProductAddOptionsObservable } from "@faire/retailer/features/ProductAdd/useCartProductAddOptions";
import { IReviewSummary } from "@faire/retailer/lib/buildReviewSummary";
import { isLoggedInRetailerOrBrandPreview } from "@faire/retailer/lib/isLoggedInRetailer";
import { getLocaleKey } from "@faire/retailer/serialized-data/getLocaleKey";
import {
  useImageGalleryHandler,
  useTrackImageChangeProductEvent,
} from "@faire/retailer/views/product/__internal__/Content/hooks";
import { ShowroomProductData } from "@faire/retailer/views/product/ProductState";
import { LazyShowroomPDPEducationSection } from "@faire/retailer/views/product/ShowroomComponents/LazyShowroomPDPEducationSection";
import { getProductOptions } from "@faire/retailer/views/product/utils/productUtils";

import { ProductDescriptionAccordions } from "./ProductDescriptionAccordions";
import { ProductAddToCartState } from "./ProductDetails/AddToCart/ProductAddToCartState";
import { ProductPromotionState } from "./ProductDetails/AddToCart/ProductPromotionsState";
import { ProductDetails } from "./ProductDetails/ProductDetails";
import { ImageGrid } from "./ProductImages/Components/ImageGrid";
import { ProductImageOrVideoViewState } from "./ProductImages/ProductImageOrVideoViewState";

interface IProps extends IViewportV2Props {
  className?: string;
  brand: IProductPageResponse.IPdpBrand;
  product: IProductPageResponse.IPdpProduct;
  badges?: IBadge[];
  brandReviewSummary: IReviewSummary | undefined;
  importShipmentInfo?: string;
  infoTexts?: IInformationMessage[];
  showroomData: ShowroomProductData | undefined;
  disclaimers?: IDisclaimer[];
  productPromotionState?: ProductPromotionState;
  preselectedProductOptionToken?: string;
  productAttributeTags?: Array<IProductsResponse.IProductAttributeTag>;
  productVideos?: IProductVideo[];
  samplesBannerDetails: ISamplesBannerDetails | undefined;
  loadTimingRef: React.RefCallback<HTMLElement>;
  afterAddToCart?: () => void;
  showSideCart?: boolean;
  productReviewSummary?: IReviewSummary;
}

const ProductContentFn: React.FC<IProps> = ({
  brand,
  product,
  className,
  brandReviewSummary,
  importShipmentInfo,
  infoTexts,
  showroomData,
  disclaimers,
  productPromotionState,
  preselectedProductOptionToken,
  badges,
  productAttributeTags,
  productVideos,
  samplesBannerDetails,
  loadTimingRef,
  afterAddToCart,
  showSideCart,
  productReviewSummary,
}) => {
  useTraceComponentRender("ProductContent");

  const { strictLocalize } = useStrictLocalization();

  const addOptions = useCartProductAddOptionsObservable();

  const productAddToCartState = useRefFrom(
    () =>
      new ProductAddToCartState(
        product,
        brand.token ?? "",
        addOptions,
        productPromotionState,
        true
      )
  ).current;

  const selectedOptionImages = useComputedObservable(
    () => productAddToCartState.selectedOption?.images ?? []
  );

  const uniqueImages = React.useMemo(() => {
    const images =
      getProductOptions(product).length === 1
        ? [...(product.visuals?.images ?? []), ...selectedOptionImages]
        : [...selectedOptionImages, ...(product.visuals?.images ?? [])];

    return uniqBy(images, (image) => image.url);
  }, [product, selectedOptionImages]);

  const productImageOrVideoViewState = useRefFrom(
    () => new ProductImageOrVideoViewState(uniqueImages, productVideos)
  ).current;

  // Effects
  useEffect(() => {
    productImageOrVideoViewState.setImages(uniqueImages);
  }, [productImageOrVideoViewState, uniqueImages]);

  useEffect(() => {
    productImageOrVideoViewState.setIndex(0);
  }, [productAddToCartState.selectedOption, productImageOrVideoViewState]);

  const showElevatedMPPProductExperience =
    !!showroomData?.isMidPricePointApparelProduct;
  const showVerticalImagery =
    showroomData?.showVerticalImagery || brand.is_apparel || false;

  useEffect(() => {
    if (showElevatedMPPProductExperience) {
      trackApparelDiscoveryShowroomV2View(
        "pdp",
        product.token ?? "",
        showVerticalImagery ? "vertical-image" : "square-image"
      );
    }
  }, [product.token, showVerticalImagery, showElevatedMPPProductExperience]);

  const { pathname } = useLocation();
  useEffect(() => {
    trackRetailersPageViewView(
      brand.token ?? "",
      product.token ?? "",
      "",
      "product",
      pathname,
      isLoggedInRetailerOrBrandPreview()
        ? "page-product-logged-in"
        : "page-product-logged-out"
    );
  }, [brand.token, product.token, pathname]);

  // A few helpers
  const { openImageGallery, closeImageGallery, isImageGalleryOpen } =
    useImageGalleryHandler();

  useTrackImageChangeProductEvent(productImageOrVideoViewState, product.token);
  const isEnglish = getLocaleKey()?.language === Language.ENG;

  const { selectedIndex } = productImageOrVideoViewState;

  return (
    <Wrapper
      className={className}
      ref={loadTimingRef}
      data-test-id="product-content"
    >
      <ImageWrapper>
        <ImageGrid
          alt={getAltTagProductPage(
            product,
            isEnglish,
            brand.name,
            strictLocalize
          )}
          openImageGallery={openImageGallery}
          setIndex={productImageOrVideoViewState.setIndex}
          showVerticalImagery={showVerticalImagery}
          productMedia={productImageOrVideoViewState.getProductMedia()}
          showroomData={showroomData}
          brandIsApparel={brand.is_apparel}
        />
      </ImageWrapper>
      {isImageGalleryOpen ? (
        <Suspense fallback={null}>
          <LazyImageGallery
            index={selectedIndex}
            productMedia={productImageOrVideoViewState.getProductMedia()}
            onChangeIndex={(index: number) =>
              productImageOrVideoViewState.setIndex(index)
            }
            onClose={closeImageGallery}
            eventName={`product/${product.token}`}
          />
        </Suspense>
      ) : null}
      <StyledProductDetails
        disclaimers={disclaimers}
        brand={brand}
        product={product}
        productAddToCartState={productAddToCartState}
        brandReviewSummary={brandReviewSummary}
        importShipmentInfo={importShipmentInfo}
        infoTexts={infoTexts}
        showElevatedMPPProductExperience={showElevatedMPPProductExperience}
        preselectedProductOptionToken={preselectedProductOptionToken}
        badges={badges}
        sampleDetails={samplesBannerDetails}
        afterAddToCart={afterAddToCart}
        showSideCart={showSideCart}
      />
      {showElevatedMPPProductExperience ? (
        <Suspense fallback={null}>
          <LazyShowroomPDPEducationSection productToken={product.token} />
        </Suspense>
      ) : null}
      <StyledProductDescriptionAccordions
        selectedOption={productAddToCartState.selectedOption}
        product={product}
        productAttributeTags={productAttributeTags ?? []}
        brand={brand}
        samplesBannerDetails={samplesBannerDetails}
        productReviewSummary={productReviewSummary}
        brandReviewSummary={brandReviewSummary}
      />
    </Wrapper>
  );
};

export const ProductContent = observer(ProductContentFn);
ProductContent.displayName = "ProductContent";

const StyledProductDescriptionAccordions = styled(ProductDescriptionAccordions)`
  ${responsiveMediaQueries.mobile} {
    width: 100vw;
  }

  grid-area: product-description-tabs;
`;

const Wrapper = styled.div`
  display: grid;
  align-items: start;
  justify-content: center;
  margin: 0 auto;

  grid-template:
    "product-images"
    "product-details"
    "product-description-tabs"
    "mpp-description"
    / 1fr;

  ${responsiveMediaQueries.tabletAndAbove} {
    grid-template:
      "focused-image            .               product-details "
      ".                        .               product-details " 16px
      "mpp-description          .               product-details " auto
      "product-description-tabs .               product-details " 1fr
      / 1fr 40px 300px;
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    grid-template:
      "focused-image            .               product-details "
      ".                        .               product-details " 16px
      "mpp-description          .               product-details " auto
      "product-description-tabs .               product-details " 1fr
      / 1fr 40px 360px;
  }
`;

const StyledProductDetails = styled(ProductDetails)`
  grid-area: product-details;
`;

const ImageWrapper = styled.div`
  position: relative;
  grid-area: focused-image;
  ${responsiveMediaQueries.mobile} {
    grid-area: product-images;
  }
`;
